<template>
    <transition
        enter-active-class="ease-out duration-300"
        enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        enter-to-class="opacity-100 translate-y-0 sm:scale-100"
        leave-active-class="ease-in duration-200"
        leave-class="opacity-100 translate-y-0 sm:scale-100"
        leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
        <div v-show="show" class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full lg:max-w-3xl" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
            <div class="text-center">
                <div class="border-t border-b border-gray-200 bg-gray-50 text-left">
                    <p class="text-sm text-gray-500 p-4" style="max-height: 24rem; overflow-y: scroll;" v-html="template" />
                </div>
            </div>
            <div v-if="shownEmailForm" class="pt-3 px-3 space-y-2">
                <p class="text-sm">
                    Please provide your e-mail address:
                </p>

                <div class="flex space-x-3">
                    <div class="flex-1">
                        <label for="email" class="block font-semibold text-gray-700 text-sm mb-1">
                            Email
                        </label>
                        <input v-model="email"
                           ref="email"
                           type="text"
                           name="email"
                           id="email"
                           :class="`py-3 px-4 rounded bg-gray-50 focus:ring-${$root.theme}-400 focus:border-${$root.theme}-400 relative block w-full rounded-none rounded-bl-md bg-transparent focus:z-10 sm:text-sm border-gray-300`"
                           placeholder="Your email">
                    </div>
                    <div class="flex-1">
                        <label for="email_confirmation" class="block font-semibold text-gray-700 text-sm mb-1">
                            Email Confirmation
                        </label>
                        <input v-model="email_confirmation"
                           ref="email_confirmation"
                           type="text"
                           name="email_confirmation"
                           id="email_confirmation"
                           :class="`py-3 px-4 rounded bg-gray-50 focus:ring-${$root.theme}-400 focus:border-${$root.theme}-400 relative block w-full rounded-none rounded-bl-md bg-transparent focus:z-10 sm:text-sm border-gray-300`"
                           placeholder="Confirm your email">
                    </div>
                </div>
            </div>
            <div v-else-if="shownSmsForm" class="pt-3 px-3 space-y-2">
                <p class="text-sm">
                    Please provide your phone number:
                </p>

                <div class="flex space-x-3">
                    <div class="flex-1">
                        <label for="phone_number" class="block font-semibold text-gray-700 text-sm mb-1">
                            Phone Number
                        </label>
                        <input v-model="phone_number"
                           ref="phone_number"
                           type="text"
                           name="phone_number"
                           id="phone_number"
                           :class="`py-3 px-4 rounded bg-gray-50 focus:ring-${$root.theme}-400 focus:border-${$root.theme}-400 relative block w-full rounded-none rounded-bl-md bg-transparent focus:z-10 sm:text-sm border-gray-300`"
                           placeholder="Your phone number">
                    </div>
                    <div class="flex-1">
                        <label for="email_confirmation" class="block font-semibold text-gray-700 text-sm mb-1">
                            Phone Number Confirmation
                        </label>
                        <input v-model="phone_number_confirmation"
                           ref="phone_number_confirmation"
                           type="text"
                           name="phone_number_confirmation"
                           id="phone_number_confirmation"
                           :class="`py-3 px-4 rounded bg-gray-50 focus:ring-${$root.theme}-400 focus:border-${$root.theme}-400 relative block w-full rounded-none rounded-bl-md bg-transparent focus:z-10 sm:text-sm border-gray-300`"
                           placeholder="Confirm your phone number">
                    </div>
                </div>
            </div>
            <div class="py-3 px-3 space-y-3">
                <div v-if="! shownEmailForm && ! shownSmsForm" class="flex space-x-3">
                    <a :href='`${$parent.consumerPortalBaseUrl}/mvn/download/${$root.tiny}`' target="_blank" @click="slowClose" type="button" class="w-full inline-flex text-gray-500 justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-white border-gray-200 text-base font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:col-start-2 sm:text-sm">
                        Download
                    </a>
                    <button v-if="canEmail" @click="showEmailForm" type="button" class="w-full inline-flex text-gray-500 justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-white border-gray-200 text-base font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:col-start-2 sm:text-sm">
                        Email Me
                    </button>
                    <button v-if="canSms" @click="showSmsForm" type="button" class="w-full inline-flex text-gray-500 justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-white border-gray-200 text-base font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:col-start-2 sm:text-sm">
                        Send to Phone
                    </button>
                </div>
                <div v-else-if="shownEmailForm" class="flex space-x-3">
                    <button
                        @click="sendEmail"
                        type="button"
                        :disabled="email !== email_confirmation || ! email || ! email_confirmation"
                        :class="{
                            [`w-full inline-flex text-white justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-${$root.theme}-600 border-gray-200 text-base font-medium hover:bg-${$root.theme}-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:col-start-2 sm:text-sm`]: true,
                            'cursor-not-allowed opacity-50': email !== email_confirmation || ! email || ! email_confirmation
                        }">
                        Send
                    </button>
                </div>
                <div v-else-if="shownSmsForm" class="flex space-x-3">
                    <button
                        @click="sendSms"
                        type="button"
                        :disabled="phone_number !== phone_number_confirmation || ! phone_number || ! phone_number_confirmation || phone_number.length !== 10 || phone_number_confirmation.length !== 10"
                        :class="{
                            [`w-full inline-flex text-white justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-${$root.theme}-600 border-gray-200 text-base font-medium hover:bg-${$root.theme}-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:col-start-2 sm:text-sm`]: true,
                            'cursor-not-allowed opacity-50': phone_number !== phone_number_confirmation || ! phone_number || ! phone_number_confirmation || phone_number.length !== 10 || phone_number_confirmation.length !== 10
                        }">
                        Send
                    </button>
                </div>
                <button @click="$emit('close')" type="button" class="w-full inline-flex text-black justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gray-200 border-gray-200 text-base font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:col-start-2 sm:text-sm">
                    Close
                </button>
            </div>
        </div>
    </transition>

</template>

<script>
    import Mixins from '../../mixins';

    export default {
        mixins: [Mixins],

        props: ['show', 'template', 'referral-email', 'referral-phone-number', 'can-email', 'can-sms'],

        mounted() {
            this.email = this.referralEmail;
            this.phone_number = this.referralPhoneNumber;
        },

        data() {
            return {
                email: '',
                email_confirmation: '',
                phone_number: '',
                phone_number_confirmation: '',
                shownSmsForm: false,
                shownEmailForm: false,
            };
        },

        watch: {
            show: {
                immediate: true,
                handler: function (show) {
                    if (show) {
                        // this.gtagEvent('show-prelegal-status:offers-page');
                    }
                }
            }
        },

        methods: {
            showSmsForm() {
                this.shownSmsForm = true;

                if (this.phone_number) {
                    setTimeout(() => this.$refs['phone_number_confirmation'].focus(), 150);
                } else {
                    setTimeout(() => this.$refs['phone_number'].focus(), 150);
                }
            },

            showEmailForm() {
                this.shownEmailForm = true;

                if (this.email) {
                    setTimeout(() => this.$refs['email_confirmation'].focus(), 150);
                } else {
                    setTimeout(() => this.$refs['email'].focus(), 150);
                }
            },

            sendEmail() {
                this.$http.post(`${this.$parent.consumerPortalBaseUrl}/api/mvn/email/${this.$root.tiny}`, {
                    email: this.email
                }).then(() => {
                    this.$emit('close');
                })
            },

            sendSms() {
                this.$http.post(`${this.$parent.consumerPortalBaseUrl}/api/mvn/sms/${this.$root.tiny}`, {
                    phone_number: this.phone_number
                }).then(() => {
                    this.$emit('close');
                })
            },

            slowClose() {
                setTimeout(() => this.$emit('close'), 750);
            }
        }
    }
</script>